// interceptors/ecdh-encryption.interceptor.ts
import { Injectable, Injector } from '@angular/core';
import {
  HttpInterceptor, HttpRequest, HttpHandler,
  HttpEvent, HttpResponse
} from '@angular/common/http';
import { Observable, from, throwError, of } from 'rxjs';
import { switchMap, catchError, mergeMap } from 'rxjs/operators';
import { ECDHService } from '../services/ecdh.service';
import { AdminAuthService } from '../admin/admin-auth.service';
import { MatDialog } from '@angular/material/dialog';
import { OpenDialog } from '../laas/dialog-for-cbs/dialog.component';

@Injectable()
export class ECDHEncryptionInterceptor implements HttpInterceptor {
  private sessionExpiredHandled: boolean = false;

  constructor(private ecdhService: ECDHService, private injector: Injector) {}

  private handle401Error(err: any, req: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (this.sessionExpiredHandled || req.url.includes('/login/logout') || req.url.includes('/login/login')) {
      return throwError(err);
    }

    this.sessionExpiredHandled = true;
    var adminAuth = this.injector.get(AdminAuthService);

    // If another tab triggered login, skip — that tab already redirected us
    if (adminAuth.isKickedByOtherTab()) {
      return throwError(err);
    }

    var dialog = this.injector.get(MatDialog);
    var errBody = err.error || {};
    var isSessionExpired = errBody.reason === 'SESSION_EXPIRED';

    var heading: string;
    var content: string;

    if (isSessionExpired) {
      adminAuth.clearSession();
      heading = 'Session Expired';
      content = 'Your session has expired. Please log in again.';
    } else {
      var userId = sessionStorage.getItem('wl-admin-id') || '';
      if (userId) {
        adminAuth.logoutFromServer(userId).subscribe(
          function () { adminAuth.adminLogout(); },
          function () { adminAuth.adminLogout(); }
        );
      } else {
        adminAuth.adminLogout();
      }
      heading = 'Unauthorized Access';
      content = 'You are not authorized. Please log in again.';
    }

    var dialogRef = dialog.open(OpenDialog, {
      data: { heading: heading, content: content },
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(function () {
      window.location.href = '/login-admin';
    });
    return new Observable<HttpEvent<any>>();
  }

  private handleSessionExpiry(body: any, req: HttpRequest<any>): void {
    if (this.sessionExpiredHandled || req.url.includes('/login/logout') || req.url.includes('/login/login')) {
      return;
    }

    var isSessionExpired = body && body.reason === 'SESSION_EXPIRED';

    if (!isSessionExpired) {
      return;
    }

    this.sessionExpiredHandled = true;
    var adminAuth = this.injector.get(AdminAuthService);

    // If another tab triggered login, skip — that tab already redirected us
    if (adminAuth.isKickedByOtherTab()) {
      return;
    }

    var dialog = this.injector.get(MatDialog);
    adminAuth.clearSession();

    var dialogRef = dialog.open(OpenDialog, {
      data: { heading: 'Session Expired', content: 'Your session has expired. Please log in again.' },
      disableClose: true
    });
    dialogRef.afterClosed().subscribe(function () {
      window.location.href = '/login-admin';
    });
  }

  private addAuthHeader(req: HttpRequest<any>): HttpRequest<any> {
    var adminAuth = this.injector.get(AdminAuthService);
    var accessToken = adminAuth.getAccessToken();
    if (accessToken && !req.url.includes('/login/login')) {
      return req.clone({
        setHeaders: { 'Authorization': 'Bearer ' + accessToken }
      });
    }
    return req;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    var self = this;

    // Never encrypt the key-exchange handshake itself, login, or logout
    if (req.url.includes('/key-exchange') || req.url.includes('/login/login') || req.url.includes('/login/logout')) {
      return next.handle(self.addAuthHeader(req)).pipe(
        catchError(function (err) {
          if (err.status === 401 || err.status === 504) {
            return self.handle401Error(err, req);
          }
          return throwError(err);
        })
      );
    }

    // Wait for ECDH key exchange to complete; also wait for token refresh unless this IS the refresh call
    var adminAuth = this.injector.get(AdminAuthService);
    var isRefreshCall = req.url.includes('/login/getnewtoken');
    var waitFor: Promise<any> = isRefreshCall
      ? this.ecdhService.whenReady()
      : Promise.all([this.ecdhService.whenReady(), adminAuth.whenAuthReady()]);
    var requestPromise: Promise<HttpRequest<any>> = waitFor.then(function () {
      // Read access token AFTER both are ready so refreshToken() has set the new token
      var authedReq = self.addAuthHeader(req);
      if (authedReq.body && authedReq.method !== 'GET') {
        console.log('[ECDH] Request payload for', authedReq.method, req.url, authedReq.body);
        return self.ecdhService.encrypt(authedReq.body).then(function (encryptedBody) {
          return authedReq.clone({
            body: encryptedBody,
            setHeaders: { 'x-session-id': self.ecdhService.getSessionId() }
          });
        });
      } else if (authedReq.method === 'GET') {
        console.log('[ECDH] Request payload for', authedReq.method, authedReq.urlWithParams);
        return authedReq.clone({
          setHeaders: { 'x-session-id': self.ecdhService.getSessionId() }
        });
      } else {
        return authedReq;
      }
    });

    // from(Promise) → switchMap into next.handle() → mergeMap to decrypt responses
    return from(requestPromise).pipe(
      switchMap(function (modifiedReq) {
        return next.handle(modifiedReq);
      }),
      mergeMap(function (event) {
        // Decrypt response if server flagged it as encrypted
        if (event instanceof HttpResponse && event.body && event.body.encrypted) {
          return from(
            self.ecdhService.decrypt(event.body.payload).then(function (decrypted) {
              console.log('[ECDH] Decrypted response for', req.url, decrypted);
              self.handleSessionExpiry(decrypted, req);
              return event.clone({ body: decrypted });
            })
          );
        }
        // For non-encrypted responses, also check
        if (event instanceof HttpResponse && event.body) {
          self.handleSessionExpiry(event.body, req);
        }
        return of(event);
      }),
      catchError(function (err) {
        console.error('[ECDH] Error:', err);
        if (err.status === 401 || err.status === 504) {
          return self.handle401Error(err, req);
        }
        return throwError(err);
      })
    );
  }
}
